import { computed, unref } from 'vue-demi'
import { LayoutModeConfig, LayoutTargetConfig } from '../types'
import { useActivePositionsMap } from './use-active-positions-map'
import { UsePartitionedItemsOptions, usePartitionedItems } from './use-partitioned-items'
import { Position, Target } from '../types/primitives'
import { MaybeRef } from '@vueuse/core'

export type UseDynamicListLayoutOptions = Pick<UsePartitionedItemsOptions, 'fallbackToAvailablePosition'>

export function useDynamicListLayout<T> (
  currentLayout: MaybeRef<LayoutModeConfig | undefined>,
  items: MaybeRef<T[]>,
  getPositionFromItem: (item: T) => Position | undefined,
  currentTargetMap: MaybeRef<Record<Target, LayoutTargetConfig>>,
  option: UseDynamicListLayoutOptions = {}
) {
  const {
    fallbackToAvailablePosition
  } = option
  const { flatActivePositions, getActivePositionsRefByTarget, activePositionsMap } = useActivePositionsMap(currentTargetMap)
  const {
    partitionedItems,
    getItemsByPositionRef,
    getItemsMapRefByPositions,
    printPartition,
    getItemPosition
  } = usePartitionedItems(
    items,
    flatActivePositions,
    getPositionFromItem,
    {
      fallbackPosition: computed(() => unref(currentLayout)?.fallbackPosition),
      fallbackToAvailablePosition
    }
  )
  function getItemsRefByTarget (name: Target) {
    const positions = getActivePositionsRefByTarget(name)
    const items = getItemsByPositionRef(positions)
    return {
      positions,
      items
    }
  }
  function getItemsMapRefByTarget (name: Target) {
    const positions = getActivePositionsRefByTarget(name)
    const itemsMap = getItemsMapRefByPositions(positions)
    return {
      positions,
      itemsMap
    }
  }

  /**
   * Find the position of the given item and the target it belongs to.
   * @param item
   * @returns
   */
  function getItemPositionAndTarget (item: T) {
    const position = getItemPosition(item)
    if (position == null) return undefined
    const target = Object.keys(unref(activePositionsMap)).find((target) => {
      return unref(activePositionsMap)[target]?.includes?.(position)
    }) as Target
    return {
      position,
      target
    }
  }

  return {
    getActivePositionsRefByTarget,
    partitionedItems,
    getItemsRefByTarget,
    getItemsMapRefByTarget,
    printPartition,
    getItemPositionAndTarget
  }
}
